import { useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import { Checkbox } from './ui/checkbox';
import { AlertTriangle, Trash2, Shield } from 'lucide-react';
import { projectId } from '../utils/supabase/info';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';

interface PurgeCompanyDataDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (wasCompanyDeleted?: boolean) => void;
  companyId: string;
  companyName: string;
  accessToken: string;
}

interface PurgeSelection {
  projects: boolean;
  configItems: boolean;
  companyRecord: boolean;
  userAccounts: boolean;
  sows: boolean;
  changeOrders: boolean;
  auditLogs: boolean;
}

export function PurgeCompanyDataDialog({
  open,
  onClose,
  onSuccess,
  companyId,
  companyName,
  accessToken,
}: PurgeCompanyDataDialogProps) {
  const [mode, setMode] = useState<'selective' | 'complete'>('selective');
  const [confirmText, setConfirmText] = useState('');
  const [understood, setUnderstood] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selection, setSelection] = useState<PurgeSelection>({
    projects: false,
    configItems: false,
    companyRecord: false,
    userAccounts: false,
    sows: false,
    changeOrders: false,
    auditLogs: false,
  });

  const confirmationText = mode === 'complete' 
    ? `PERMANENTLY DELETE ${companyName}` 
    : `DELETE PathKit`;
  const hasSelection = mode === 'complete' || Object.values(selection).some(v => v);
  const isConfirmed = confirmText === confirmationText && understood && hasSelection;

  const handleToggle = (key: keyof PurgeSelection) => {
    setSelection(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSelectAll = () => {
    const allSelected = Object.values(selection).every(v => v);
    const newValue = !allSelected;
    setSelection({
      projects: newValue,
      configItems: newValue,
      companyRecord: newValue,
      userAccounts: newValue,
      sows: newValue,
      changeOrders: newValue,
      auditLogs: newValue,
    });
  };

  const getRetainedItems = () => {
    const items = [];
    if (!selection.projects) items.push('Jira Spaces (Projects)');
    if (!selection.configItems) items.push('Configuration Items (Teams, Workflows, Work Types, Request Types, Fields)');
    if (!selection.companyRecord) items.push('Company record');
    if (!selection.userAccounts) items.push('User accounts');
    if (!selection.sows) items.push('SOWs (Statements of Work)');
    if (!selection.changeOrders) items.push('Change Orders');
    if (!selection.auditLogs) items.push('Audit logs');
    return items;
  };

  const handlePurge = async () => {
    if (!isConfirmed) return;

    setLoading(true);
    setError('');

    try {
      const endpoint = mode === 'complete'
        ? `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/admin/delete-company-complete/${companyId}`
        : `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/admin/purge-company-data/${companyId}`;

      const response = await fetch(endpoint, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ selection }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to purge company data');
      }

      const result = await response.json();
      const wasCompanyDeleted = mode === 'complete' || result.companyDeleted;

      // If company was deleted and user is part of this company, redirect to login
      if (wasCompanyDeleted) {
        // Check if current user is part of the deleted company
        const currentUser = JSON.parse(localStorage.getItem('pathkit_user') || '{}');
        if (currentUser.companyId === companyId) {
          alert('Company account has been deleted. You will be logged out.');
          localStorage.removeItem('pathkit_access_token');
          localStorage.removeItem('pathkit_user');
          localStorage.removeItem('pathkit_company');
          window.location.href = '/';
          return;
        }
      }

      onSuccess(wasCompanyDeleted);
      handleClose();
    } catch (err: any) {
      console.error('Error purging company data:', err);
      setError(err.message || 'Failed to purge company data');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setMode('selective');
    setConfirmText('');
    setUnderstood(false);
    setError('');
    setSelection({
      projects: false,
      configItems: false,
      companyRecord: false,
      userAccounts: false,
      sows: false,
      changeOrders: false,
      auditLogs: false,
    });
    onClose();
  };

  const retainedItems = getRetainedItems();

  return (
    <AlertDialog open={open} onOpenChange={handleClose}>
      <AlertDialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            Delete Company Data - {companyName}
          </AlertDialogTitle>
          <AlertDialogDescription>
            This is a destructive action that cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <Tabs value={mode} onValueChange={(v) => setMode(v as 'selective' | 'complete')}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="selective">Selective Data Purge</TabsTrigger>
            <TabsTrigger value="complete" className="text-destructive">
              <Shield className="h-4 w-4 mr-2" />
              Complete Account Deletion
            </TabsTrigger>
          </TabsList>

          <div className="space-y-4 mt-4">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <TabsContent value="selective" className="space-y-4 mt-0">
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  <div className="flex items-center justify-between mb-2">
                    <strong>Warning: Select what to permanently delete:</strong>
                    <button
                      type="button"
                      onClick={handleSelectAll}
                      className="text-sm underline hover:no-underline"
                    >
                      {Object.values(selection).every(v => v) ? 'Deselect All' : 'Select All'}
                    </button>
                  </div>
                  <div className="space-y-2 ml-2">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="projects"
                        checked={selection.projects}
                        onCheckedChange={() => handleToggle('projects')}
                      />
                      <Label htmlFor="projects" className="cursor-pointer">
                        All Jira Spaces (Projects)
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="configItems"
                        checked={selection.configItems}
                        onCheckedChange={() => handleToggle('configItems')}
                      />
                      <Label htmlFor="configItems" className="cursor-pointer">
                        All Configuration Items (Teams, Workflows, Work Types, Request Types, Fields)
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="companyRecord"
                        checked={selection.companyRecord}
                        onCheckedChange={() => handleToggle('companyRecord')}
                      />
                      <Label htmlFor="companyRecord" className="cursor-pointer">
                        Company record
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="userAccounts"
                        checked={selection.userAccounts}
                        onCheckedChange={() => handleToggle('userAccounts')}
                      />
                      <Label htmlFor="userAccounts" className="cursor-pointer">
                        User accounts
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="sows"
                        checked={selection.sows}
                        onCheckedChange={() => handleToggle('sows')}
                      />
                      <Label htmlFor="sows" className="cursor-pointer">
                        SOWs (Statements of Work)
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="changeOrders"
                        checked={selection.changeOrders}
                        onCheckedChange={() => handleToggle('changeOrders')}
                      />
                      <Label htmlFor="changeOrders" className="cursor-pointer">
                        Change Orders
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="auditLogs"
                        checked={selection.auditLogs}
                        onCheckedChange={() => handleToggle('auditLogs')}
                      />
                      <Label htmlFor="auditLogs" className="cursor-pointer">
                        Audit logs
                      </Label>
                    </div>
                  </div>
                </AlertDescription>
              </Alert>

              {retainedItems.length > 0 && (
                <Alert>
                  <AlertDescription>
                    <strong>The following will be retained:</strong>
                    <ul className="list-disc ml-6 mt-2 space-y-1 text-muted-foreground">
                      {retainedItems.map((item, index) => (
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}

              <div className="space-y-4 border-t pt-4">
                <div className="flex items-start space-x-2">
                  <Checkbox
                    id="understand"
                    checked={understood}
                    onCheckedChange={(checked) => setUnderstood(checked as boolean)}
                  />
                  <Label
                    htmlFor="understand"
                    className="cursor-pointer leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    I understand that this action is permanent and cannot be undone.
                    All selected data will be lost.
                  </Label>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirm">
                    Type <strong>{confirmationText}</strong> to confirm:
                  </Label>
                  <Input
                    id="confirm"
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder={confirmationText}
                    disabled={!understood || !hasSelection}
                  />
                  <p className="text-muted-foreground">
                    This confirmation is case-sensitive
                  </p>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="complete" className="space-y-4 mt-0">
              <Alert variant="destructive">
                <Shield className="h-4 w-4" />
                <AlertDescription>
                  <strong className="block mb-2">Right to Erasure - Complete Account Deletion</strong>
                  <p className="mb-3">
                    This will <strong>PERMANENTLY DELETE EVERYTHING</strong> associated with {companyName}:
                  </p>
                  <ul className="list-disc ml-6 space-y-1">
                    <li><strong>Company Record</strong> - The company will cease to exist</li>
                    <li><strong>All User Accounts</strong> - Every user will lose access (authentication deleted)</li>
                    <li><strong>All Jira Spaces (Projects)</strong></li>
                    <li><strong>All Configuration Items (Teams, Workflows, Work Types, Request Types, Fields)</strong></li>
                    <li><strong>All SOWs and Change Orders</strong></li>
                    <li><strong>All Audit Logs</strong></li>
                    <li><strong>All Custom Domains and Subdomains</strong></li>
                  </ul>
                  <p className="mt-3 font-bold text-destructive-foreground">
                    Nothing will remain. This company will be completely erased from PathKit.
                  </p>
                </AlertDescription>
              </Alert>

              <Alert className="border-amber-500 bg-amber-50">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-amber-900">
                  <strong>Use this option for:</strong>
                  <ul className="list-disc ml-6 mt-2 space-y-1">
                    <li>GDPR "Right to be Forgotten" requests</li>
                    <li>Company account closure/cancellation</li>
                    <li>Removing test/demo accounts</li>
                  </ul>
                </AlertDescription>
              </Alert>

              <div className="space-y-4 border-t pt-4">
                <div className="flex items-start space-x-2">
                  <Checkbox
                    id="understand-complete"
                    checked={understood}
                    onCheckedChange={(checked) => setUnderstood(checked as boolean)}
                  />
                  <Label
                    htmlFor="understand-complete"
                    className="cursor-pointer leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    <strong className="text-destructive">
                      I understand this will PERMANENTLY DELETE the entire company account,
                      all user accounts, and ALL data with no possibility of recovery.
                      This action complies with GDPR erasure requirements.
                    </strong>
                  </Label>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirm-complete">
                    Type <strong className="text-destructive">{confirmationText}</strong> to confirm complete deletion:
                  </Label>
                  <Input
                    id="confirm-complete"
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder={confirmationText}
                    disabled={!understood}
                    className="border-destructive"
                  />
                  <p className="text-muted-foreground">
                    This confirmation is case-sensitive and must match exactly
                  </p>
                </div>
              </div>
            </TabsContent>
          </div>
        </Tabs>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={loading}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handlePurge}
            disabled={!isConfirmed || loading}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            <Trash2 className="mr-2 h-4 w-4" />
            {loading 
              ? (mode === 'complete' ? 'Deleting Account...' : 'Purging Data...') 
              : (mode === 'complete' ? 'Delete Account Completely' : 'Purge Selected Data')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}